import { Box, Button, Collapse, Skeleton, Table, Text } from "@mantine/core";
import { Page, PageHeader } from "../../components/layout/Page";
import { ArrowClockwiseIcon, CaretDownIcon, CaretRightIcon, CheckIcon, InfoIcon, WarningIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useSwr from "swr";
import { LedgerApi } from "../../apis";
import { IIntercompanyReport } from "../../apis/ledger";
import { MoneyCell } from "../../components/list";
import { Notice } from "../../components/ui/notice";
import { StatusBadge } from "../../components/ui/status-badge";
import { currencyFormat } from "../../utils";
import classes from "./intercompany.module.css";

/**
 * Хоорондын тооцоо (SPRINT-06 §8.2).
 *
 * ---------------------------------------------------------------------------
 * ЭНЭ ДЭЛГЭЦИЙН ХАМГИЙН ЧУХАЛ ХЭСЭГ НЬ `ok` БИШ.
 *
 * Тайлан `ok: true` буцаасан ч дотроо хоёр чимээгүй цэг агуулна:
 *
 *   • `unledgeredPairs` — нэг тал нь дэвтэргүй тул шалгалтад ОРООГҮЙ хос.
 *     Энэ нь §0.2b-ийн зориудын байдал, алдаа биш. Гэвч үл үзэгдэх байвал
 *     «шалгаагүй» гэдэг нь «шалгаад цэвэр» мэт харагдана (аюул A2b).
 *   • `unresolvedRefs` — refCode нь ямар ч бизнестэй тааралгүй үлдсэн.
 *     refCode массаар солигдвол дэлгэц «0 хос, ok» гэж харуулаад сохроор
 *     ногоон болно.
 *
 * Хоёулаа `ok`-ыг УНАГААХГҮЙ — тэдгээр нь батлагдсан зөрүү биш. Гэхдээ тоо нь
 * үргэлж харагдаж, дарж жагсаалт нь нээгдэнэ.
 * ---------------------------------------------------------------------------
 */

type Tone = "ok" | "warn" | "bad";

const TONE_COLOR: Record<Tone, string> = { ok: "green", warn: "yellow", bad: "red" };

type PairRow = { key: string; a: string; b: string; v3: number; v4: number; diff: number };

/** Зөрүүтэй хосуудын хүснэгт — зөрүү байхгүй бол нэг мөр «Зөрүүгүй». */
const PairsTable = ({ heads, rows }: { heads: [string, string, string, string]; rows: PairRow[] }) => {
  const { t } = useTranslation(["billing"]);
  const diffs = rows.filter((x) => x.diff !== 0);

  if (diffs.length === 0) {
    return <Box className={classes.body}>{t("billing:ledger.interco.noDiff")}</Box>;
  }

  return (
    <Box className={classes.wrap}>
      <Table className={classes.table} data-density="compact" withRowBorders={false} horizontalSpacing={0} verticalSpacing={0}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{heads[0]}</Table.Th>
            <Table.Th>{heads[1]}</Table.Th>
            <Table.Th ta="right">{heads[2]}</Table.Th>
            <Table.Th ta="right">{heads[3]}</Table.Th>
            <Table.Th ta="right">{t("billing:ledger.interco.colDiff")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {diffs.map((x) => (
            <Table.Tr key={x.key}>
              <Table.Td><Text span className={classes.strong}>{x.a}</Text></Table.Td>
              <Table.Td><Text span className={classes.strong}>{x.b}</Text></Table.Td>
              <Table.Td ta="right"><MoneyCell amount={x.v3} /></Table.Td>
              <Table.Td ta="right"><MoneyCell amount={x.v4} /></Table.Td>
              <Table.Td ta="right"><MoneyCell amount={x.diff} emphasis /></Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Box>
  );
};

/**
 * Canvas: шалгалт бүр нэг жагсаалтын мөр (төлвийн дүрс, гарчиг, тайлбар, баруун талд дүн).
 * Мөрийг дарахад зөрүүний дэлгэрэнгүй нээгдэнэ; зөрүүтэй шалгалт анхнаасаа нээлттэй.
 */
const CheckRow = ({
  title, desc, label, tone, children,
}: {
  title: string;
  desc: string;
  label: string;
  tone: Tone;
  children?: React.ReactNode;
}) => {
  const [open, setOpen] = useState(tone === "bad");
  return (
    <Box className={classes.checkItem}>
      <Box component="button" type="button" className={classes.checkRow} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <Box component="span" className={classes.checkIcon} data-tone={tone} aria-hidden>
          {tone === "ok" ? <CheckIcon size={14} weight="bold" /> : <WarningIcon size={14} weight="bold" />}
        </Box>
        <Box component="span" className={classes.checkText}>
          <Text span className={classes.checkTitle}>{title}</Text>
          <Text span className={classes.checkDesc}>{desc}</Text>
        </Box>
        <Text span className={classes.checkLabel} data-tone={tone}>{label}</Text>
        {open ? <CaretDownIcon size={12} weight="bold" /> : <CaretRightIcon size={12} weight="bold" />}
      </Box>
      <Collapse expanded={open}>{children}</Collapse>
    </Box>
  );
};

/** Тоо нь харагдаж, дарж жагсаалт нь нээгддэг блок. */
const Disclosure = ({ label, count, children }: { label: string; count: number; children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Box component="button" type="button" className={classes.disc} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        {open ? <CaretDownIcon size={12} weight="bold" /> : <CaretRightIcon size={12} weight="bold" />}
        <Text span>{label}</Text>
        <StatusBadge color="gray" uppercase={false} label={String(count)} />
      </Box>
      <Collapse expanded={open}>
        <Box className={classes.discBody}>{children}</Box>
      </Collapse>
    </>
  );
};

export const LedgerIntercompanyPage = () => {
  const { t } = useTranslation(["billing", "common"]);

  const { data, isLoading, isValidating, error, mutate } = useSwr<IIntercompanyReport>(
    "ledger.intercompany",
    async () => await LedgerApi.getIntercompany(),
    { revalidateOnFocus: false },
  );

  const header = (
    <PageHeader
      title={t("billing:ledger.interco.title")}
      subtitle={t("billing:ledger.interco.subtitle")}
      extra={
        <Button
          variant="default"
          loading={isValidating && !isLoading}
          leftSection={<ArrowClockwiseIcon size={14} />}
          onClick={() => mutate()}>
          {t("billing:ledger.interco.recheck")}
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <Page>
        {header}
        <Skeleton h={60} />
        <Skeleton h={200} />
      </Page>
    );
  }

  if (error || !data) {
    return (
      <Page>
        {header}
        {error ? <Notice tone="danger" role="alert">{t("billing:ledger.interco.loadFailed")}</Notice> : null}
      </Page>
    );
  }

  const pairCount = data.receivablePairs.length + data.intercoPairs.length + data.consignmentPairs.length;
  const nBad = (rows: { diff: number }[]) => rows.filter((x) => x.diff !== 0).length;
  const pairsLabel = (rows: { diff: number }[]) =>
    nBad(rows) === 0
      ? t("billing:ledger.interco.pairsCount", { count: rows.length })
      : t("billing:ledger.interco.diffCount", { count: nBad(rows) });
  const pairsTone = (rows: { diff: number }[]): Tone => (nBad(rows) === 0 ? "ok" : "bad");

  const commissionTone: Tone = data.commission.diff !== 0 ? "bad" : data.commission.scopeComplete ? "ok" : "warn";
  const lonelyTone: Tone = data.lonelyMirrorGroups.length === 0 ? "ok" : "bad";

  const checks: { key: string; title: string; desc: string; label: string; tone: Tone }[] = [
    { key: "1", title: t("billing:ledger.interco.check1"), desc: t("billing:ledger.interco.check1Desc"), label: pairsLabel(data.receivablePairs), tone: pairsTone(data.receivablePairs) },
    { key: "2", title: t("billing:ledger.interco.check2"), desc: t("billing:ledger.interco.check2Desc"), label: pairsLabel(data.intercoPairs), tone: pairsTone(data.intercoPairs) },
    {
      key: "3",
      title: t("billing:ledger.interco.check3"),
      desc: t("billing:ledger.interco.check3Desc"),
      label: lonelyTone === "ok"
        ? t("billing:ledger.interco.okLabel")
        : t("billing:ledger.interco.lonelyCount", { count: data.lonelyMirrorGroups.length }),
      tone: lonelyTone,
    },
    {
      key: "4",
      title: t("billing:ledger.interco.check4"),
      desc: t("billing:ledger.interco.commissionDetail", {
        expense: currencyFormat(data.commission.expense),
        revenue: currencyFormat(data.commission.revenue),
      }),
      label: currencyFormat(data.commission.diff),
      tone: commissionTone,
    },
    { key: "5", title: t("billing:ledger.interco.check5"), desc: t("billing:ledger.interco.check5Desc"), label: pairsLabel(data.consignmentPairs), tone: pairsTone(data.consignmentPairs) },
  ];
  const badChecks = checks.filter((c) => c.tone === "bad").length;
  // `key` stays out of the spread: React warns when a props object carrying `key` is spread into JSX.
  const rowProps = ({ title, desc, label, tone }: (typeof checks)[number]) => ({ title, desc, label, tone });

  return (
    <Page>
      {header}

      <Box className={classes.stack}>
        <Box className={classes.card}>
          {pairCount === 0 && (
            <Box className={classes.head}>
              <Text className={classes.sub}>{t("billing:ledger.interco.noPairs")}</Text>
            </Box>
          )}

          {/* 1 — S.RECV == R.PAYABLE */}
          <CheckRow {...rowProps(checks[0])}>
            <PairsTable
              heads={[
                t("billing:ledger.interco.colBusinessA"),
                t("billing:ledger.interco.colBusinessB"),
                t("billing:ledger.interco.colReceivable"),
                t("billing:ledger.interco.colPayable"),
              ]}
              rows={data.receivablePairs.map((x) => ({
                key: `${x.businessId}-${x.counterpartyId}`,
                a: x.businessName, b: x.counterpartyName, v3: x.receivable, v4: x.payable, diff: x.diff,
              }))}
            />
          </CheckRow>

          {/* 2 — A.INTERCO == -B.INTERCO */}
          <CheckRow {...rowProps(checks[1])}>
            <PairsTable
              heads={[
                t("billing:ledger.interco.colBusinessA"),
                t("billing:ledger.interco.colBusinessB"),
                t("billing:ledger.interco.colBalanceA"),
                t("billing:ledger.interco.colBalanceB"),
              ]}
              rows={data.intercoPairs.map((x) => ({
                key: `${x.businessId}-${x.counterpartyId}`,
                a: x.businessName, b: x.counterpartyName, v3: x.balance, v4: x.counterBalance, diff: x.diff,
              }))}
            />
          </CheckRow>

          {/* 3 — mirror groups with at least two sides */}
          <CheckRow {...rowProps(checks[2])}>
            {data.lonelyMirrorGroups.length === 0 ? (
              <Box className={classes.body}>{t("billing:ledger.interco.noDiff")}</Box>
            ) : (
              <Box className={classes.body}>
                {data.lonelyMirrorGroups.slice(0, 20).map((g) => (
                  <Text span key={g.mirrorGroupId}>
                    {t("billing:ledger.interco.lonelyRow", { id: g.mirrorGroupId.slice(0, 8), count: g.entries })}
                  </Text>
                ))}
              </Box>
            )}
          </CheckRow>

          {/* 4 — commission (expense/revenue already in the row description) */}
          <CheckRow {...rowProps(checks[3])}>
            {data.commission.scopeComplete ? (
              <Box className={classes.body}>{t("billing:ledger.interco.noDiff")}</Box>
            ) : (
              <Box className={classes.bodyNotice}>
                <Notice>{t("billing:ledger.interco.commissionScope")}</Notice>
              </Box>
            )}
          </CheckRow>

          {/* 5 — INV-CONSIGN == CONSIGN-MEMO */}
          <CheckRow {...rowProps(checks[4])}>
            <PairsTable
              heads={[
                t("billing:ledger.interco.colOwner"),
                t("billing:ledger.interco.colHolder"),
                t("billing:ledger.interco.colOwnerStock"),
                t("billing:ledger.interco.colHolderMemo"),
              ]}
              rows={data.consignmentPairs.map((x) => ({
                key: `${x.businessId}-${x.counterpartyId}`,
                a: x.businessName, b: x.counterpartyName, v3: x.ownerStock, v4: x.holderMemo, diff: x.diff,
              }))}
            />
          </CheckRow>

          <Box className={classes.summaryFoot}>
            <Text span className={badChecks === 0 ? classes.footOk : classes.footBad}>
              {badChecks === 0
                ? t("billing:ledger.interco.summaryOk", { count: checks.length })
                : t("billing:ledger.interco.summaryBad", { count: badChecks })}
            </Text>
            <Text span className={classes.footNote}>
              {t("billing:ledger.interco.notCheckedSummary", {
                unledgered: data.unledgeredPairs.length + data.unledgeredMirrorGroups.length,
                unresolved: data.unresolvedRefs.length,
                ambiguous: data.ambiguousRefs.length,
              })}
            </Text>
          </Box>
        </Box>

        {/*
          The two counters that would otherwise be invisible. Neither is red,
          and neither affects `ok` - but a screen that only showed `ok` would
          hide both, and that is precisely how a monitoring screen goes blind.
        */}
        <Box className={classes.card}>
          <Box className={classes.head}>
            <Text className={classes.title}>{t("billing:ledger.interco.notCheckedTitle")}</Text>
          </Box>

          <Disclosure
            label={t("billing:ledger.interco.unledgeredLabel")}
            count={data.unledgeredPairs.length + data.unledgeredMirrorGroups.length}>
            <Text span>{t("billing:ledger.interco.unledgeredNote")}</Text>
            {data.unledgeredPairs.slice(0, 20).map((x, i) => (
              <Text span className={classes.mono} key={`${x.businessId}-${x.counterpartyId}-${i}`}>
                {x.businessId.slice(0, 8)} - {x.counterpartyId.slice(0, 8)} · {x.role}
              </Text>
            ))}
            {data.unledgeredMirrorGroups.length > 0 && (
              <Text span>{t("billing:ledger.interco.unledgeredGroups", { count: data.unledgeredMirrorGroups.length })}</Text>
            )}
          </Disclosure>

          <Disclosure label={t("billing:ledger.interco.unresolvedLabel")} count={data.unresolvedRefs.length}>
            <Text span>{t("billing:ledger.interco.unresolvedNote")}</Text>
            {data.unresolvedRefs.slice(0, 20).map((x, i) => (
              <Text span className={classes.mono} key={`${x.roleRef}-${i}`}>{x.role} · {x.roleRef}</Text>
            ))}
          </Disclosure>

          <Disclosure label={t("billing:ledger.interco.ambiguousLabel")} count={data.ambiguousRefs.length}>
            <Text span>{t("billing:ledger.interco.ambiguousNote")}</Text>
            {data.ambiguousRefs.slice(0, 20).map((x, i) => (
              <Text span className={classes.mono} key={`${x.roleRef}-${i}`}>
                {t("billing:ledger.interco.ambiguousRow", { role: x.role, ref: x.roleRef, count: x.matches })}
              </Text>
            ))}
          </Disclosure>
        </Box>

        <Box className={classes.hint}>
          <InfoIcon size={14} weight="bold" />
          <Text span>{t("billing:ledger.interco.consolidationHint")}</Text>
        </Box>
      </Box>
    </Page>
  );
};

export default LedgerIntercompanyPage;
